<template>
  <div class="card">
    <h5 class="card-header bg-primary text-white">Collaborators</h5>
    <div class="card-body p-0">
      <ul class="list-group list-group-flush">
        <li class="list-group-item d-flex" v-for="c in investigation.collaborators" :key="c.userId">
          <fa icon="user" fixed-width size="sm" class="text-primary mr-1 mt-1" />{{ c.fullName
          }}<button v-if="canRemove(c)" type="button" class="btn btn-sm btn-danger ml-auto" @click="removeCollaborator(c)">
            <fa icon="times" fixed-width />
            <span class="mx-1">Remove</span>
          </button>
        </li>
      </ul>
    </div>
    <div class="card-footer" v-if="isClosed">
      <div class="alert alert-dark mb-0" role="alert">
        You may not add or remove collaborators on a closed investigation.
      </div>
    </div>
    <div class="card-footer" v-if="!isClosed">
      <ErrorSummary :error="error" />
      <form ref="form" class="mt-3 needs-validation" :class="formWasValidated ? 'was-validated' : ''" novalidate @submit.prevent>
        <div class="row">
          <div class="col">
            <div class="form-group">
              <multiselect
                v-model="newInvestigationCollaborators"
                label="fullName"
                track-by="userId"
                placeholder="Type to search"
                open-direction="bottom"
                :options="organizationUsers"
                :multiple="true"
                :searchable="true"
                :loading="organizationUsersLoading"
                :internal-search="false"
                :clear-on-select="false"
                :close-on-select="false"
                :options-limit="300"
                :limit="3"
                :limit-text="investigationCollaboratorsLimitText"
                :max-height="600"
                :show-no-results="false"
                :hide-selected="true"
                @search-change="getOrganizationCollaborators"
              >
                <template slot="tag" slot-scope="{ option, remove }"
                  ><span class="badge badge-primary p-1 mr-1"
                    ><span>{{ option.fullName }}</span
                    ><fa icon="times" class="cursor-pointer ml-1" @click="remove(option)"/></span
                ></template>
              </multiselect>
            </div>
          </div>
          <div class="mr-3">
            <button
              type="button"
              class="btn btn-sm btn-primary mt-1"
              @click="addCollaborator"
              :disabled="loadingMessage || !newInvestigationCollaborators.length"
              @keyup.enter="addCollaborator"
            >
              <span v-if="loadingMessage" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
              <fa v-if="!loadingMessage" icon="plus" fixed-width />
              <span class="mx-1">{{ loadingMessage ? loadingMessage : "Add" }}</span>
            </button>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import Multiselect from "vue-multiselect";
import "vue-multiselect/dist/vue-multiselect.min.css";

import { mapGetters } from "vuex";
import formatter from "@/utils/formatter";
import ErrorSummary from "@/components/ErrorSummary.vue";

export default {
  components: {
    ErrorSummary,
    Multiselect,
  },
  props: {
    isLoading: Boolean,
    investigation: Object,
  },
  data() {
    return {
      // form
      loadingMessage: null,
      formWasValidated: false,
      error: null,

      // users
      organizationUsers: [],
      organizationUsersLoading: false,

      // add
      newInvestigationCollaborators: [],
    };
  },
  computed: {
    ...mapGetters("user", ["user"]),
    formatter: () => formatter,
    isClosed() {
      return this.investigation.statusCode === "closed";
    },
  },
  watch: {
    investigation() {
      const promises = [this.getOrganizationCollaborators()];
      Promise.all(promises).then(() => (this.loadingMessage = null));
    },
  },
  methods: {
    canRemove(collaborator) {
      return !this.isSelf(collaborator) && !this.isCreator(collaborator) && !this.isClosed;
    },
    isSelf(collaborator) {
      return this.user && collaborator.userId === this.user.id;
    },
    isCreator(collaborator) {
      return collaborator.userId === this.investigation.createdByUserId;
    },
    getOrganizationCollaborators(query) {
      var encodedQuery = query ? encodeURIComponent(query) : "";
      if (!this.investigation) return Promise.resolve([]);
      this.organizationUsersLoading = true;
      return this.$axios
        .get(`/v1/organizations/${this.investigation.organizationId}/investigations/collaborators?query=${encodedQuery}`)
        .then(res => {
          var collaboratorUserIds = this.investigation.collaborators.map(c => c.userId);
          this.organizationUsers = res.data.items.filter(i => !collaboratorUserIds.includes(i.userId));
        })
        .then(() => (this.organizationUsersLoading = false))
        .catch(err => (this.error = err));
    },
    removeCollaborator(collaborator) {
      this.$axios
        .delete(
          `/v1/organizations/${this.investigation.organizationId}/investigations/${this.investigation.investigationId}/collaborators/${collaborator.userId}`
        )
        .catch(err => (this.error = err))
        .finally(() => this.$emit("reload"));
    },
    addCollaboratorReset() {
      this.loadingMessage = null;
      this.formWasValidated = false;
      this.error = null;
    },
    addCollaborator() {
      // reset
      this.addCollaboratorReset();

      // validations
      if (!this.$refs.form.checkValidity()) {
        this.formWasValidated = true;
        return;
      }

      // collaborator user ids
      var collaboratorUserIds = this.newInvestigationCollaborators.map(u => u.userId);

      // payload
      const payload = {
        collaboratorUserIds: collaboratorUserIds,
      };

      // api
      this.loadingMessage = "Saving...";
      this.organizationUsersLoading = true;
      this.$axios
        .post(`/v1/organizations/${this.investigation.organizationId}/investigations/${this.investigation.investigationId}/collaborators`, payload)
        .then(() => (this.newInvestigationCollaborators = []))
        .catch(err => (this.error = err))
        .finally(() => {
          this.loadingMessage = null;
          this.organizationUsersLoading = false;
          this.$emit("reload");
        });
    },
    investigationCollaboratorsLimitText(count) {
      return `and ${count} other collaborators`;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/styles/base.scss";
</style>
